import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContexte';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import useServerZones from '../../hooks/useServerZones';
import api from '../../api';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './ReservationSheetPage.css';

/**
 * Fiche de réservation en cours (espace membre) : mes zones en haut, échange de zone quand la
 * phase d'échange est ouverte, puis toutes les réservations de la fiche.
 */
const ReservationSheetPage = () => {
    const { serverId } = useParams();
    const { user } = useAuth();
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const { availableZones } = useServerZones(serverId);
    const userId = user?.id;

    const [session, setSession] = useState(null);
    const [rows, setRows] = useState([]);
    const [auctionWins, setAuctionWins] = useState([]);   // zones remportées aux enchères sur cette fiche
    const [auctionRunning, setAuctionRunning] = useState(false);   // enchère de la fiche encore ouverte
    const [names, setNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [swapping, setSwapping] = useState(null);       // réservation dont on change la zone
    const [zoneSearch, setZoneSearch] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [tableSearch, setTableSearch] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/reservations/active/${serverId}`);
            const { session: s, reservations, auctionWins: wins, auctionRunning: running } = res.data;
            setSession(s);
            setRows(reservations || []);
            setAuctionWins(wins || []);
            setAuctionRunning(!!running);
            const ids = [...new Set([...(reservations || []), ...(wins || [])].map(r => r.joueurId).filter(Boolean))];
            if (ids.length > 0) {
                api.post('/players/display-names', { playerIds: ids, serverId })
                    .then(nr => { if (nr.data?.success) setNames(nr.data.displayNames || {}); })
                    .catch(() => {});
            }
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [serverId]);

    useEffect(() => { load(); }, [load]);

    const myRows = rows.filter(r => r.joueurId === userId);
    const myWins = auctionWins.filter(w => w.joueurId === userId);
    const takenZones = useMemo(
        () => new Set([...rows, ...auctionWins].filter(r => r.zone).map(r => r.zone)),
        [rows, auctionWins]
    );
    const freeZones = availableZones.filter(z => !takenZones.has(z.zone));
    const swapOpen = !!session?.swapWindowOpen;

    const query = zoneSearch.trim().toLowerCase();
    const visibleFreeZones = query
        ? freeZones.filter(z => z.zone.toLowerCase().includes(query) || (z.donjon || '').toLowerCase().includes(query))
        : freeZones;

    const getName = (id) => names[id] || (id ? `${id.slice(0, 8)}…` : '—');
    const tableQuery = tableSearch.trim().toLowerCase();
    const visibleRows = rows
        .map((r, index) => ({ ...r, rank: index + 1 }))
        .filter(r => !tableQuery
            || getName(r.joueurId).toLowerCase().includes(tableQuery)
            || (r.zone || '').toLowerCase().includes(tableQuery)
            || (r.donjon || '').toLowerCase().includes(tableQuery));

    const confirmSwap = async (zone) => {
        if (!swapping) return;
        if (!window.confirm(t('sheet.confirmSwap', 'Échanger « {from} » contre « {to} » ?', { from: swapping.zone || t('sheet.noZone', 'aucune zone'), to: zone.zone }))) return;
        setSubmitting(true);
        try {
            await api.post(`/reservations/swap/${serverId}/${session._id}`, {
                newZone: zone.zone,
                newDonjon: zone.donjon || '',
                reservationId: swapping._id
            });
            success(t('sheet.swapDone', 'Ta zone est maintenant « {zone} »', { zone: zone.zone }));
            setSwapping(null);
            setZoneSearch('');
            await load();
        } catch (err) {
            showError(err?.response?.data?.error || t('sheet.swapError', 'L\'échange n\'a pas pu être fait'));
            await load();
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('sheet.loading', 'Chargement de la fiche...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader icon="clipboard" title={t('member.nav.sheet', 'Fiche de réservation')} subtitle={session?.title} />

            {loadError ? (
                <p className="sheet-error" role="alert">{t('sheet.loadError', 'Impossible de charger la fiche en cours.')}</p>
            ) : !session ? (
                <div className="c2-card c2-empty sheet-empty">
                    <Icon name="clipboard" size={36} />
                    <p>{t('sheet.noSheet', 'Aucune fiche de réservation en cours pour le moment.')}</p>
                </div>
            ) : (
                <>
                    {/* ── Statut + mes zones ── */}
                    <section className="c2-card c2-section sheet-mine">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="map" size={20} />{t('sheet.myZones', 'Mes zones')}</h2>
                            <div className="sheet-status">
                                {swapOpen ? (
                                    <span className="c2-chip c2-chip--green"><Icon name="swap" size={14} />{t('sheet.swapOpen', 'Phase d\'échange en cours')}</span>
                                ) : (
                                    <span className="c2-chip"><Icon name="lock" size={14} />{t('sheet.swapClosed', 'Phase d\'échange terminée')}</span>
                                )}
                                {session.frozen && <span className="c2-chip c2-chip--purple"><Icon name="lock" size={14} />{t('sheet.frozen', 'Fiche figée')}</span>}
                            </div>
                        </div>

                        {myRows.length === 0 && myWins.length === 0 ? (
                            <p className="c2-muted">{t('sheet.notRegistered', 'Tu n\'es pas inscrit sur cette fiche.')}</p>
                        ) : (
                            <ul className="sheet-mine__list">
                                {myRows.map(r => (
                                    <li key={r._id} className={`c2-tile sheet-mine__zone${swapping?._id === r._id ? ' is-swapping' : ''}`}>
                                        <span className="sheet-mine__icon"><Icon name="map" size={18} /></span>
                                        <span className="sheet-mine__text">
                                            <strong>{r.zone || t('sheet.noZoneYet', 'Pas encore de zone')}</strong>
                                            {r.donjon && <span className="c2-muted">{r.donjon}</span>}
                                        </span>
                                        {swapOpen && (
                                            <button
                                                type="button"
                                                className={`c2-btn c2-btn--sm ${swapping?._id === r._id ? 'c2-btn--ghost' : 'c2-btn--accent'}`}
                                                onClick={() => { setSwapping(swapping?._id === r._id ? null : r); setZoneSearch(''); }}
                                            >
                                                {swapping?._id === r._id ? t('sheet.cancel', 'Annuler') : t('sheet.changeZone', 'Changer de zone')}
                                            </button>
                                        )}
                                    </li>
                                ))}
                                {myWins.map(w => (
                                    <li key={`enchere-${w.zone}`} className="c2-tile sheet-mine__zone sheet-mine__zone--auction">
                                        <span className="sheet-mine__icon"><Icon name="hammer" size={18} /></span>
                                        <span className="sheet-mine__text">
                                            <strong>{w.zone}</strong>
                                            {w.donjon && <span className="c2-muted">{w.donjon}</span>}
                                        </span>
                                        <span className="c2-chip c2-chip--purple">{t('sheet.auction', 'Enchère')} · {w.amount.toLocaleString(getLocale())} k</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* ── Choix d'une zone libre ── */}
                        {swapOpen && swapping && (
                            <div className="sheet-swap">
                                <div className="sheet-swap__head">
                                    <span>
                                        {t('sheet.pickFreeZone', 'Choisis une zone libre pour remplacer')} <strong>{swapping.zone || t('sheet.yourSpot', 'ta place')}</strong>
                                        <span className="c2-muted"> · {freeZones.length > 1 ? t('sheet.freeCountPlural', '{n} libres', { n: freeZones.length }) : t('sheet.freeCount', '{n} libre', { n: freeZones.length })}</span>
                                    </span>
                                    <input
                                        type="search"
                                        className="c2-input c2-input--sm sheet-swap__search"
                                        placeholder={t('sheet.searchZone', 'Rechercher une zone...')}
                                        aria-label={t('sheet.searchFreeZone', 'Rechercher une zone libre')}
                                        value={zoneSearch}
                                        onChange={e => setZoneSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                {visibleFreeZones.length === 0 ? (
                                    <p className="c2-muted">{freeZones.length === 0 ? t('sheet.allTaken', 'Toutes les zones sont prises.') : t('sheet.noFreeMatch', 'Aucune zone libre ne correspond.')}</p>
                                ) : (
                                    <ul className="sheet-swap__zones">
                                        {visibleFreeZones.map(z => (
                                            <li key={z.zone}>
                                                <button type="button" className="sheet-swap__zone" onClick={() => confirmSwap(z)} disabled={submitting}>
                                                    <strong>{z.zone}</strong>
                                                    {z.donjon && <span>{z.donjon}</span>}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </section>

                    {/* ── Zones libres (après échanges et enchères) ── */}
                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="map" size={20} />{t('sheet.freeZones', 'Zones libres')}</h2>
                            {!auctionRunning && (
                                <span className="c2-chip c2-chip--green">{freeZones.length > 1 ? t('sheet.freeCountPlural', '{n} libres', { n: freeZones.length }) : t('sheet.freeCount', '{n} libre', { n: freeZones.length })}</span>
                            )}
                        </div>
                        {auctionRunning ? (
                            <p className="c2-muted sheet-free__note">
                                <Icon name="hammer" size={16} />
                                {t('sheet.freeAfterAuction', 'Des enchères sont en cours sur cette fiche : les zones libres seront affichées une fois les enchères clôturées.')}
                            </p>
                        ) : freeZones.length === 0 ? (
                            <p className="c2-muted">{t('sheet.allTaken', 'Toutes les zones sont prises.')}</p>
                        ) : (
                            <>
                                {swapOpen && <p className="c2-muted sheet-free__note">{t('sheet.freeMayChange', 'Les échanges sont ouverts : cette liste peut encore changer.')}</p>}
                                <ul className="sheet-free__zones">
                                    {freeZones.map(z => (
                                        <li key={z.zone} className="sheet-free__zone" title={z.donjon || undefined}>
                                            {z.zone}
                                            {z.donjon && <span> · {z.donjon}</span>}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </section>

                    {/* ── Toutes les réservations ── */}
                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="list" size={20} />{t('sheet.allReservations', 'Toutes les réservations')}</h2>
                            <div className="sheet-tools">
                                <span className="c2-chip">{rows.length > 1 ? t('sheet.reservationsCount', '{n} réservations', { n: rows.length }) : t('sheet.reservationCount', '{n} réservation', { n: rows.length })}</span>
                                <input
                                    type="search"
                                    className="c2-input c2-input--sm sheet-tools__search"
                                    placeholder={t('sheet.searchTablePlaceholder', 'Joueur ou zone...')}
                                    aria-label={t('sheet.searchTable', 'Rechercher dans la fiche')}
                                    value={tableSearch}
                                    onChange={e => setTableSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="c2-table-wrap">
                            <table className="c2-table sheet-table">
                                <thead>
                                    <tr>
                                        <th className="is-center">#</th>
                                        <th>{t('sheet.player', 'Joueur')}</th>
                                        <th>{t('sheet.zone', 'Zone')}</th>
                                        <th>{t('sheet.dungeon', 'Donjon')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.map(r => {
                                        const isMe = r.joueurId === userId;
                                        return (
                                            <tr key={r._id || r.rank} className={isMe ? 'is-me' : undefined}>
                                                <td className="is-center c2-muted">{r.rank}</td>
                                                <td>
                                                    {getName(r.joueurId)}
                                                    {isMe && <span className="c2-chip c2-chip--green sheet-table__me">{t('sheet.me', 'moi')}</span>}
                                                </td>
                                                <td>{r.zone ? <strong>{r.zone}</strong> : <span className="c2-muted">—</span>}</td>
                                                <td className="c2-muted">{r.donjon || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                    {auctionWins
                                        .filter(w => !tableQuery
                                            || getName(w.joueurId).toLowerCase().includes(tableQuery)
                                            || w.zone.toLowerCase().includes(tableQuery))
                                        .map(w => (
                                            <tr key={`enchere-${w.zone}`} className={`sheet-table__auction${w.joueurId === userId ? ' is-me' : ''}`}>
                                                <td className="is-center"><Icon name="hammer" size={14} /></td>
                                                <td>
                                                    {getName(w.joueurId)}
                                                    {w.joueurId === userId && <span className="c2-chip c2-chip--green sheet-table__me">{t('sheet.me', 'moi')}</span>}
                                                </td>
                                                <td><strong>{w.zone}</strong> <span className="c2-chip c2-chip--purple sheet-table__me">{t('sheet.auctionChip', 'enchère')}</span></td>
                                                <td className="c2-muted">{w.donjon || '—'}</td>
                                            </tr>
                                        ))}
                                    {visibleRows.length === 0 && (
                                        <tr><td colSpan={4} className="is-center c2-muted">{t('common.noResults', 'Aucun résultat')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default ReservationSheetPage;
